import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from './config/firebase';
import Layout from './components/Layout';
import Auth from './components/Auth';
import Dashboard from './pages/Dashboard';
import Portfolio from './pages/Portfolio';
import Watchlist from './pages/Watchlist';
import Recommendations from './pages/Recommendations';
import Admin from './pages/Admin';
import TradingRulesPage from './pages/TradingRules';
import Network from './pages/Network';
import Help from './pages/Help';
import Feedback from './pages/Feedback';
import ProfileSettings from './components/ProfileSettings';
import BetaAccess from './pages/BetaAccess';
import AdminUsageDashboard from './pages/AdminUsageDashboard';
import { RecommendationService } from './services/recommendationService';
import { UserProvider } from './contexts/UserContext';
import { UserService } from './services/userService';
import { BetaAccessService } from './services/betaAccessService';
import { UserProfile } from './types';
import ProtectedRoute from './components/ProtectedRoute';
import DebugSharing from './pages/DebugSharing';
import './utils/testRecommendations'; // Make test utility available in console
import './utils/debugWatchlist'; // Make debug utility available in console
import './utils/migrateToMultiTenancy'; // Make migration utility available in console
import './utils/debugSharing'; // Make debug sharing utility available in console
import './utils/resetWelcome'; // Make reset welcome utility available in console
import './utils/debugProfile'; // Make debug profile utility available in console

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      
      if (user) {
        try {
          // Create user profile if needed
          await UserService.createUserProfile(user);
          
          // Load user profile
          const userProfile = await UserService.getUserProfile(user.uid);
          setProfile(userProfile);
          
          // Initialize recommendation service
          await RecommendationService.initialize();
          console.log('Recommendation service initialized');
          
          // Initialize beta access list if admin
          if (userProfile?.isAdmin) {
            console.log('Admin user logged in');
            await BetaAccessService.initializeBetaUsers();
          }
        } catch (error) {
          console.error('Error initializing user:', error);
        }
      } else {
        setProfile(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Auth user={user} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <UserProvider user={user} profile={profile}>
        <Router>
          <Layout 
            user={user} 
            isAdmin={profile?.isAdmin || false}
            profilePicUrl={profile?.profilePicUrl}
            displayName={profile?.displayName}
          >
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/portfolio/:id" element={<Portfolio />} />
              <Route path="/watchlist" element={<Watchlist />} />
              <Route path="/recommendations" element={<Recommendations />} />
              <Route path="/rules" element={<TradingRulesPage />} />
              <Route path="/network" element={<Network />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/profile" element={<ProfileSettings />} />
              <Route path="/help" element={<Help />} />
              <Route path="/debug-sharing" element={<DebugSharing />} />
              <Route path="/admin" element={
                <ProtectedRoute requireAdmin>
                  <Admin />
                </ProtectedRoute>
              } />
              <Route path="/admin/beta-access" element={
                <ProtectedRoute requireAdmin>
                  <BetaAccess />
                </ProtectedRoute>
              } />
              <Route path="/admin/usage" element={
                <ProtectedRoute requireAdmin>
                  <AdminUsageDashboard />
                </ProtectedRoute>
              } />
            </Routes>
          </Layout>
        </Router>
      </UserProvider>
    </ThemeProvider>
  );
}

export default App;
